import { useEffect, useState } from "react";
import { useRequest } from "./useRequest";

export type AttachedDesignUpload = {
  id: number;
  customName: string;
  date: string;
  path: string;
};

type DesignUploadField = "proposalId" | "tallyId" | "pullingDesignId";

interface Props {
  endpointBase: "proposal-list" | "tally-list" | "pulling-list";
  designId?: number | null;
  designIdField: DesignUploadField;
  wellId?: number | null;
  enabled?: boolean;
}

const isSuccessful = (response: any) => response?.success || response?.succes;

export const useAttachedDesignUpload = ({
  endpointBase,
  designId,
  designIdField,
  wellId,
  enabled = true,
}: Props) => {
  const { handleRequest } = useRequest();
  const [attachedUpload, setAttachedUpload] =
    useState<AttachedDesignUpload | null>(null);
  const [isLoadingAttachedUpload, setIsLoadingAttachedUpload] = useState(false);
  const [refreshIndex, setRefreshIndex] = useState(0);

  useEffect(() => {
    if (!enabled || !designId || !wellId) {
      setAttachedUpload(null);
      return;
    }

    setIsLoadingAttachedUpload(true);
    handleRequest({
      endpoint: `${endpointBase}/${wellId}`,
      options: { method: "GET" },
    })
      .then((response) => {
        const rows = isSuccessful(response) && Array.isArray(response.data)
          ? response.data
          : [];
        const newestUpload = rows
          .filter((row: any) => row[designIdField] === designId)
          .sort(
            (a: any, b: any) =>
              new Date(b.date).getTime() - new Date(a.date).getTime(),
          )[0];

        setAttachedUpload(newestUpload ?? null);
      })
      .catch(() => setAttachedUpload(null))
      .finally(() => setIsLoadingAttachedUpload(false));
  }, [designId, designIdField, enabled, endpointBase, refreshIndex, wellId]);

  return {
    attachedUpload,
    isLoadingAttachedUpload,
    refreshAttachedUpload: () => setRefreshIndex((current) => current + 1),
  };
};
